import { FC } from "react";
import dayjs from "dayjs";

/**
 * Tarjeta de sistema dentro del hilo de una solicitud de Expertise.
 *
 * No es un mensaje que alguien escribio: es un evento con estructura (fecha original, fecha
 * propuesta, quien lo genero). Por eso el backend lo guarda tipado y en claro, y aqui se pinta
 * como tarjeta en vez de burbuja — conservando avatar, autor, hora y el pie de acciones del
 * mensaje que la contiene.
 *
 * Las acciones NO se derivan aqui. Quien puede reagendar y quien puede confirmar depende del
 * estado del hilo y de quien hizo la ultima propuesta, y eso solo lo sabe el contenedor; la
 * tarjeta se limita a pintar lo que le habiliten.
 */
type Props = {
    /** 'expertise_request' | 'expertise_reschedule' | 'expertise_confirmed' */
    messageType: string;
    /** Nombre de quien genero el evento. */
    actorName?: string;
    originalDate?: string | null;
    proposedDate?: string | null;
    /** Habilita RESCHEDULE: la solicitud sigue abierta y esta es la tarjeta vigente del hilo. */
    canReschedule?: boolean;
    /** Habilita CONFIRM: hay una propuesta viva y NO la hizo quien esta mirando. */
    canConfirm?: boolean;
    /** Estado efectivo de la solicitud: request | accepted | declined | unanswered. */
    status?: string;
    onConfirm?: () => void;
    onReschedule?: () => void;
}

const fmt = (value?: string | null) =>
    value ? dayjs(value).format('DD / MM / YYYY - h:mma') : '—';

/**
 * Solo se anuncia el desenlace cuando la solicitud ya NO admite respuesta. Mientras sigue en
 * `request` no se dice nada: el estado ya es evidente por los botones.
 */
const CLOSED_NOTES: Record<string, string> = {
    accepted: 'This request was accepted.',
    declined: 'This request was declined.',
    unanswered: 'This request expired without an answer.',
};

const TITLES: Record<string, string> = {
    expertise_request: 'Expertise Request',
    expertise_reschedule: 'Request Reschedule',
    expertise_confirmed: 'Reschedule Confirmed',
};

export const ExpertiseRequestCard: FC<Props> = ({
    messageType, actorName, originalDate, proposedDate, canReschedule, canConfirm, status,
    onConfirm, onReschedule,
}) => {
    const isReschedule = messageType === 'expertise_reschedule';
    /**
     * Solo hay dos fechas que mostrar mientras la propuesta sigue VIVA.
     *
     * Al aceptarla, el backend mueve la fecha propuesta a `scheduled_on` y limpia `proposed_on`:
     * desde ese momento existe una sola fecha, la acordada. Seguir rotulandola "Original Date" y
     * dejando un "Proposed Date" vacio decia justo lo contrario de lo que paso.
     *
     * La fecha original previa a la reagenda NO se conserva en la base — se sobrescribe. Si algun
     * dia hace falta ese historial, hay que guardarlo en el modelo, no derivarlo aqui.
     */
    const hasLiveProposal = isReschedule && Boolean(proposedDate);
    const isConfirmed = messageType === 'expertise_confirmed';
    const who = actorName && actorName !== 'You' ? actorName : 'The other party';
    const closedNote = status ? CLOSED_NOTES[status] : undefined;
    // Cerrada: ya no hay nada que responder, asi que tampoco tiene sentido ofrecer acciones.
    const showReschedule = Boolean(canReschedule) && !closedNote;
    const showConfirm = Boolean(canConfirm) && !closedNote;

    return (
        <div className="expertise-chat-card">
            <span className="expertise-chat-card__tag">{TITLES[messageType] ?? 'Expertise'}</span>

            <span className="expertise-chat-card__lead">
                {isReschedule && `${who} ${hasLiveProposal ? 'is requesting' : 'requested'} to reschedule`}
                {isConfirmed && `${who} confirmed the new date`}
                {!isReschedule && !isConfirmed && `${who} requested this expertise`}
            </span>

            <div className="expertise-chat-card__row">
                <span className="expertise-chat-card__label">
                    {hasLiveProposal ? 'Original Date' : 'Date'}
                </span>
                <span className="expertise-chat-card__value">{fmt(originalDate)}</span>
            </div>

            {hasLiveProposal && (
                <div className="expertise-chat-card__row">
                    <span className="expertise-chat-card__label">Proposed Date</span>
                    <span className="expertise-chat-card__value">{fmt(proposedDate)}</span>
                </div>
            )}

            {closedNote && (
                <span className={`expertise-chat-card__state expertise-chat-card__state--${status}`}>
                    {closedNote}
                </span>
            )}

            {(showReschedule || showConfirm) && (
                <div className="d-flex flex-row justify-content-between w-100 expertise-chat-card__actions">
                    {showReschedule ? (
                    <button type="button" className="expertise-chat-card__reschedule" onClick={onReschedule}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M6.66797 1.66406V4.16406" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.332 1.66406V4.16406" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2.91797 7.57812H17.0846" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17.5 7.08073V14.1641C17.5 16.6641 16.25 18.3307 13.3333 18.3307H6.66667C3.75 18.3307 2.5 16.6641 2.5 14.1641V7.08073C2.5 4.58073 3.75 2.91406 6.66667 2.91406H13.3333C16.25 2.91406 17.5 4.58073 17.5 7.08073Z" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.0781 11.4141H13.0856" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.0781 13.9141H13.0856" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.99609 11.4141H10.0036" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.99609 13.9141H10.0036" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6.91016 11.4141H6.91764" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6.91016 13.9141H6.91764" stroke="#151515" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Reschedule</span>
                    </button>
                    ) : <span />}

                    {showConfirm && (
                    <button type="button" className="expertise-chat-card__confirm" onClick={onConfirm}>
                        <span>Confirm</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                            <path d="M8.5 0.382812L12 3.38696H0" stroke="white"/>
                        </svg>
                    </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default ExpertiseRequestCard;
